import { createAction } from 'actions/utils'
import * as applicationActions from 'actions/application'
import { users } from 'services'
import USER_SETTINGS from 'constants/userSettings'
import {
  eulaErrorHandler,
  pageErrorHandler,
  mutationErrorHandler,
} from 'sagas/errorHandlers'

export const SHOW_RELEASE_NOTES = 'SHOW_RELEASE_NOTES'
export const showReleaseNotes = createAction(SHOW_RELEASE_NOTES, ['show'])

export const UPDATE_USER_SETTING = 'UPDATE_USER_SETTING'
export const UPDATE_USER_SETTING_SUCCESS = 'UPDATE_USER_SETTING_SUCCESS'
export const UPDATE_USER_SETTING_SUCCESS_TABLE_ORDER =
  'UPDATE_USER_SETTING_SUCCESS_TABLE_ORDER'
export const UPDATE_USER_SETTING_FAIL = 'UPDATE_USER_SETTING_FAIL'
export const updateUserSetting = (setting, value, settingName) =>
  applicationActions.apiAction({
    asyncFn: users.updateUserSettings,
    successActionType:
      USER_SETTINGS.DASHBOARD_TABLES_ORDER === setting
        ? UPDATE_USER_SETTING_SUCCESS_TABLE_ORDER
        : UPDATE_USER_SETTING_SUCCESS,
    failureActionType: UPDATE_USER_SETTING_FAIL,
    requestActionType: UPDATE_USER_SETTING,
    id: UPDATE_USER_SETTING,
    args: {
      settings: {
        [setting]: value,
        ...(settingName && { settingName }),
      },
    },
  })

export const UPDATE_USER_SETTINGS = 'UPDATE_USER_SETTINGS'
export const UPDATE_USER_SETTINGS_SUCCESS = 'UPDATE_USER_SETTINGS_SUCCESS'
export const UPDATE_USER_SETTINGS_FAIL = 'UPDATE_USER_SETTINGS_FAIL'
export const updateUserSettings = (settings) =>
  applicationActions.apiAction({
    asyncFn: users.updateUserSettings,
    successActionType: UPDATE_USER_SETTINGS_SUCCESS,
    failureActionType: UPDATE_USER_SETTINGS_FAIL,
    requestActionType: UPDATE_USER_SETTINGS,
    id: UPDATE_USER_SETTINGS,
    args: { settings },
  })

export const EULA_DISCLAIMER_ACCEPTED = 'EULA_DISCLAIMER_ACCEPTED'
export const EULA_DISCLAIMER_UPDATE_FAIL = 'EULA_DISCLAIMER_UPDATE_FAIL'
export const acceptEulaDisclaimer = () =>
  applicationActions.apiAction({
    asyncFn: users.acceptEula,
    successActionType: EULA_DISCLAIMER_ACCEPTED,
    failureActionType: EULA_DISCLAIMER_UPDATE_FAIL,
    id: EULA_DISCLAIMER_ACCEPTED,
    errorHandlers: [eulaErrorHandler],
  })

export const RECENTLY_VIEWED_STORAGE_KEY = 'chec_recently_viewed_vehicles'

export const getRecentlyViewedStorageKey = (userName) =>
  userName
    ? `${RECENTLY_VIEWED_STORAGE_KEY}_${userName}`
    : RECENTLY_VIEWED_STORAGE_KEY

export const REQUESTING_RECENTLY_VIEWED_DATA = 'REQUESTING_RECENTLY_VIEWED_DATA'

export const ADD_VEHICLE_TO_RECENTLY_VIEWED = 'ADD_VEHICLE_TO_RECENTLY_VIEWED'
export const addVehicleToRecentlyViewed = (vehicle) => ({
  vehicle,
  type: ADD_VEHICLE_TO_RECENTLY_VIEWED,
})

export const TOGGLE_RECENTLY_VIEWED_SELECTED = 'TOGGLE_RECENTLY_VIEWED_SELECTED'
export const toggleRecentlyViewedSelected = (index, resource) => ({
  index,
  resource,
  subResource: 'recentlyViewedVehicles',
  type: TOGGLE_RECENTLY_VIEWED_SELECTED,
})

export const CLEAR_RECENTLY_VIEWED_SELECTED = 'CLEAR_RECENTLY_VIEWED_SELECTED'
export const clearRecentlyViewedSelected = (resource) => ({
  resource,
  subResource: 'recentlyViewedVehicles',
  type: CLEAR_RECENTLY_VIEWED_SELECTED,
})

export const RECENTLY_VIEWED_DATA_RECEIVED = 'RECENTLY_VIEWED_DATA_RECEIVED'

export const RECENTLY_VIEWED_DATA_NOT_RECEIVED =
  'RECENTLY_VIEWED_DATA_NOT_RECEIVED'

export const APPLY_RECENTLY_VIEWED_LOCAL_CACHE =
  'APPLY_RECENTLY_VIEWED_LOCAL_CACHE'
export const applyRecentlyViewedLocalCache = (vehicles) => ({
  type: APPLY_RECENTLY_VIEWED_LOCAL_CACHE,
  vehicles,
})

export const GET_USER_INFO = 'GET_USER_INFO'
export const getUser = () =>
  applicationActions.apiAction({
    asyncFn: users.getUser,
    successActionType: GET_USER_INFO,
    id: GET_USER_INFO,
  })

export const requestRecentlyViewedData = () =>
  applicationActions.apiAction({
    asyncFn: users.getRecentlyViewed,
    successActionType: RECENTLY_VIEWED_DATA_RECEIVED,
    failureActionType: RECENTLY_VIEWED_DATA_NOT_RECEIVED,
    requestActionType: REQUESTING_RECENTLY_VIEWED_DATA,
    id: REQUESTING_RECENTLY_VIEWED_DATA,
    // Non-critical: failure only affects the recently-viewed panel.
    // Suppress the blocking ErrorModal so the user can continue normally.
    errorHandlers: [pageErrorHandler],
  })

export const REQUESTING_USER_FAVORITE_VEHICLES =
  'REQUESTING_USER_FAVORITE_VEHICLES'

export const USER_FAVORITE_VEHICLES_RECEIVED = 'USER_FAVORITE_VEHICLES_RECEIVED'

export const USER_FAVORITE_VEHICLES_NOT_RECEIVED =
  'USER_FAVORITE_VEHICLES_NOT_RECEIVED'

export const requestUserFavoriteVehicles = () =>
  applicationActions.apiAction({
    asyncFn: users.getFavoriteVehicles,
    successActionType: USER_FAVORITE_VEHICLES_RECEIVED,
    failureActionType: USER_FAVORITE_VEHICLES_NOT_RECEIVED,
    requestActionType: REQUESTING_USER_FAVORITE_VEHICLES,
    id: REQUESTING_USER_FAVORITE_VEHICLES,
    // Non-critical: failure only affects the favorites panel.
    // Suppress the blocking ErrorModal so the user can continue normally.
    errorHandlers: [pageErrorHandler],
  })

export const FAVORITING_USER_VEHICLE = 'FAVORITING_USER_VEHICLE'

// Note: ADDING_VEHICLE_TO_FAVORITE and REMOVING_VEHICLE_FROM_FAVORITE were
// removed — they were used to key addToAsyncSet/removeFromAsyncSet signals in
// the old thunk implementation. After migration to apiAction the async-set is
// managed automatically via the callSignal derived from successActionType + id.
// App.jsx now uses getIsProcessingFavoritedVehicle instead.

export const USER_VEHICLE_FAVORITED = 'USER_VEHICLE_FAVORITED'

export const FAVORITE_USER_VEHICLE_FAILED = 'FAVORITE_USER_VEHICLE_FAILED'

/**
 * Add a vehicle to the user's favorites list.
 *
 * Uses `id: vehicle.serialNumber` so that concurrent favorite operations on
 * different vehicles get distinct callSignals and don't de-duplicate each other.
 *
 * **Known limitation — global loading flag:** The reducer uses a single boolean
 * `processingFavoritedVehicle` to track in-flight state. With per-vehicle IDs,
 * multiple vehicles can be favorited/unfavorited concurrently, and `processingFavoritedVehicle`
 * will clear to `false` as soon as ANY one completes — even if others are still
 * in-flight. `App.jsx` uses this flag for a full-screen loading overlay, which
 * may dismiss prematurely in concurrent scenarios. This is an accepted cosmetic
 * trade-off; the data integrity of each individual favorite operation is not affected.
 */
export const favoriteVehicle = (vehicle) =>
  applicationActions.apiAction({
    asyncFn: users.addFavoriteVehicle,
    args: { serialNumber: vehicle.serialNumber, vehicle },
    successActionType: USER_VEHICLE_FAVORITED,
    failureActionType: FAVORITE_USER_VEHICLE_FAILED,
    requestActionType: FAVORITING_USER_VEHICLE,
    // use serialNumber as the id so concurrent favorite operations on
    // different vehicles get distinct callSignals and don't block each other.
    id: vehicle.serialNumber,
  })

export const REMOVING_FAVORITED_USER_VEHICLE = 'REMOVING_FAVORITED_USER_VEHICLE'

export const USER_VEHICLE_FAVORITE_REMOVED = 'USER_VEHICLE_FAVORITE_REMOVED'

export const USER_VEHICLE_FAVORITE_REMOVAL_FAILED =
  'USER_VEHICLE_FAVORITE_REMOVAL_FAILED'

export const removeVehicleFavorite = (serialNumber) =>
  applicationActions.apiAction({
    asyncFn: users.removeFavoriteVehicle,
    args: { serialNumber },
    successActionType: USER_VEHICLE_FAVORITE_REMOVED,
    failureActionType: USER_VEHICLE_FAVORITE_REMOVAL_FAILED,
    requestActionType: REMOVING_FAVORITED_USER_VEHICLE,
    // Use serialNumber as the id so concurrent remove operations on
    // different vehicles get distinct callSignals and don't block each other.
    id: serialNumber,
  })

export const UPDATING_VEHICLE_FAVORITE = 'UPDATING_VEHICLE_FAVORITE'

export const USER_VEHICLE_FAVORITE_UPDATED = 'USER_VEHICLE_FAVORITE_UPDATED'

export const USER_VEHICLE_FAVORITE_UPDATE_FAILED =
  'USER_VEHICLE_FAVORITE_UPDATE_FAILED'

export const updateVehicleFavorite = (serialNumber, customDescription) =>
  applicationActions.apiAction({
    asyncFn: users.updateFavoriteVehicle,
    args: { serialNumber, customDescription },
    successActionType: USER_VEHICLE_FAVORITE_UPDATED,
    failureActionType: USER_VEHICLE_FAVORITE_UPDATE_FAILED,
    requestActionType: UPDATING_VEHICLE_FAVORITE,
    id: serialNumber,
  })

export const SET_FAVORITE_VEHICLES_FLYOUT_VISIBLE =
  'SET_FAVORITE_VEHICLES_FLYOUT_VISIBLE'
export const setFavoriteVehiclesFlyoutVisible = (visible) => ({
  visible,
  type: SET_FAVORITE_VEHICLES_FLYOUT_VISIBLE,
})

export const CLEAR_FAVORITE_ADDED_NOTIFICATION =
  'CLEAR_FAVORITE_ADDED_NOTIFICATION'
export const clearFavoriteAddedNotification = () => ({
  type: CLEAR_FAVORITE_ADDED_NOTIFICATION,
})

export const CLEAR_FAVORITE_REMOVED_NOTIFICATION =
  'CLEAR_FAVORITE_REMOVED_NOTIFICATION'
export const clearFavoriteRemovedNotification = () => ({
  type: CLEAR_FAVORITE_REMOVED_NOTIFICATION,
})

export const CLEAR_FAVORITE_UPDATED_NOTIFICATION =
  'CLEAR_FAVORITE_UPDATED_NOTIFICATION'
export const clearFavoriteUpdatedNotification = () => ({
  type: CLEAR_FAVORITE_UPDATED_NOTIFICATION,
})

export const CLEAR_FAVORITE_ERROR_NOTIFICATION =
  'CLEAR_FAVORITE_ERROR_NOTIFICATION'
export const clearFavoriteErrorNotification = () => ({
  type: CLEAR_FAVORITE_ERROR_NOTIFICATION,
})

export const REORDERING_FAVORITE_VEHICLES = 'REORDERING_FAVORITE_VEHICLES'
const reorderingFavoriteVehicles = () => ({
  type: REORDERING_FAVORITE_VEHICLES,
})

export const FAVORITE_VEHICLES_REORDERED = 'FAVORITE_VEHICLES_REORDERED'
const favoriteVehiclesReordered = (vehicles) => ({
  vehicles,
  type: FAVORITE_VEHICLES_REORDERED,
})

export const FAVORITE_VEHICLES_REORDER_FAILED =
  'FAVORITE_VEHICLES_REORDER_FAILED'

/**
 * Sentinel type for the API call that confirms a reorder on the server.
 *
 * A separate type (not FAVORITE_VEHICLES_REORDERED) is used deliberately:
 * the generic API success action built by apiCall contains `serialNumbers` and
 * `data` — it does NOT carry a `vehicles` key. The reducer for
 * FAVORITE_VEHICLES_REORDERED reads `action.vehicles`, so reusing that type
 * would overwrite the already-correct optimistic list with `undefined`.
 *
 * The UI is updated optimistically; this confirmation action is dispatched
 * only so reducers can clear any in-flight state if needed. No reducer
 * currently handles it, which is intentional.
 */
export const FAVORITE_VEHICLES_REORDER_CONFIRMED =
  'FAVORITE_VEHICLES_REORDER_CONFIRMED'

/**
 * Optimistically reorder the favorite vehicles list and confirm the new order
 * with the server via a fire-and-forget apiAction.
 *
 * @param {object[]} vehicles - The vehicles array in the desired new order.
 * @returns {void} - Intentionally fire-and-forget. The UI is updated
 *   optimistically; there is no promise to await. Uses a static de-dup id
 *   (REORDERING_FAVORITE_VEHICLES) so at most one reorder is in-flight at a
 *   time — this prevents a race where a failed earlier reorder's rollback
 *   overwrites the ordering committed by a later successful reorder.
 */
export const reorderFavoriteVehicles = (vehicles) => (dispatch, getState) => {
  // Guard: if a reorder is already in-flight, the apiAction will be de-duplicated
  // by apiCall.js (same static id). Without this guard, the loading state would be
  // incremented but never decremented — because de-dup skips both success and
  // failure actions, leaving favoritingCount leaked and the overlay stuck forever.
  // Bail early so the optimistic update and loading increment only happen when the
  // call will actually fire.
  //
  // We check favoritingCount > 0 (not just reorderingFavoriteVehicles) because two
  // synchronous dispatches arriving before Redux processes the first one can both
  // read reorderingFavoriteVehicles === false — a classic "check-then-act" race.
  // favoritingCount is incremented by dispatch(reorderingFavoriteVehicles()) and
  // that increment is also visible in the snapshot only after Redux processes it,
  // but we read from getState() *before* the first dispatch, so the initial check
  // is still safe. The key property is: if favoritingCount > 0 then at least one
  // reorder is already in flight and we must not increment again.
  const userState = getState().get('user')
  const alreadyReordering = userState.get('reorderingFavoriteVehicles')
  const favoritingCount = userState.get('favoritingCount') || 0
  if (alreadyReordering || favoritingCount > 0) return

  // Snapshot the current order before the optimistic update so we can restore
  // it if the API call fails. The failure reducer reads action.previousVehicles
  // (spread from args by apiCall.js) and reverts the list.
  const previousVehicles = getState()
    .getIn(['user', 'favoriteVehicles'])
    ?.toJS()

  dispatch(reorderingFavoriteVehicles())
  // Optimistically update the UI
  dispatch(favoriteVehiclesReordered(vehicles))

  const serialNumbers = vehicles.map((v) => v.serialNumber)

  dispatch(
    applicationActions.apiAction({
      asyncFn: users.reorderFavoriteVehicles,
      args: { serialNumbers, previousVehicles },
      successActionType: FAVORITE_VEHICLES_REORDER_CONFIRMED,
      failureActionType: FAVORITE_VEHICLES_REORDER_FAILED,
      // Use a static id so that concurrent reorders are de-duplicated to one
      // in-flight request at a time. This prevents the race condition where a
      // second reorder's rollback reverts a first reorder that already succeeded:
      // if a reorder is in-flight the second call resolves with undefined and
      // the second optimistic update is the only visible change (safe). The UX
      // trade-off is minimal — users rarely drag twice faster than one API round-trip.
      id: REORDERING_FAVORITE_VEHICLES,
    })
  )
}

export const REQUESTING_ALERT = 'REQUESTING_ALERT'
export const ALERT_RECEIVED = 'ALERT_RECEIVED'
export const ALERT_NOT_RECEIVED = 'ALERT_NOT_RECEIVED'
export const requestAlert = () =>
  applicationActions.apiAction({
    asyncFn: users.getAlerts,
    successActionType: ALERT_RECEIVED,
    failureActionType: ALERT_NOT_RECEIVED,
    requestActionType: REQUESTING_ALERT,
    id: REQUESTING_ALERT,
    // Non-critical: failure only affects the alert banner display.
    // Suppress the blocking ErrorModal so the user can continue normally.
    errorHandlers: [pageErrorHandler],
  })

export const SAVING_ALERT = 'SAVING_ALERT'
export const ALERT_SAVED = 'ALERT_SAVED'
export const ALERT_NOT_SAVED = 'ALERT_NOT_SAVED'

export const saveAlerts = (alertsData) =>
  applicationActions.apiAction({
    asyncFn: users.updateAlerts,
    successActionType: ALERT_SAVED,
    failureActionType: ALERT_NOT_SAVED,
    requestActionType: SAVING_ALERT,
    id: SAVING_ALERT,
    args: alertsData,
  })

// update store for alerts
export const SET_ALERT = 'SET_ALERT'
export const setAlert = (data) => ({
  data,
  type: SET_ALERT,
})

// ── Role management ──────────────────────────────────────────────────────────

export const UPDATE_USER_ROLES_REQUEST = 'UPDATE_USER_ROLES_REQUEST'
export const UPDATE_USER_ROLES_SUCCESS = 'UPDATE_USER_ROLES_SUCCESS'
export const UPDATE_USER_ROLES_FAILURE = 'UPDATE_USER_ROLES_FAILURE'

/**
 * Update the current user's own roles.
 * On success the `watchUpdateUserRoles` saga dispatches `getUser()` to
 * refresh the user state and shows a success toast.
 *
 * @param {string[]} roles - The full list of roles to set.
 */
export const updateUserRoles = (roles) =>
  applicationActions.apiAction({
    asyncFn: users.updateUserRoles,
    args: { roles },
    successActionType: UPDATE_USER_ROLES_SUCCESS,
    failureActionType: UPDATE_USER_ROLES_FAILURE,
    requestActionType: UPDATE_USER_ROLES_REQUEST,
    // Use a stable descriptive string instead of the action type constant so
    // that renaming UPDATE_USER_ROLES_REQUEST or UPDATE_USER_ROLES_SUCCESS
    // doesn't silently break the loading-key computation in RoleEditor.jsx.
    // The loading key is: callSignal = successActionType + id
    //   = UPDATE_USER_ROLES_SUCCESS + 'update-own-roles'
    id: 'update-own-roles',
  })

export const CHANGE_USER_ROLE_REQUEST = 'CHANGE_USER_ROLE_REQUEST'
export const CHANGE_USER_ROLE_SUCCESS = 'CHANGE_USER_ROLE_SUCCESS'
export const CHANGE_USER_ROLE_FAILURE = 'CHANGE_USER_ROLE_FAILURE'

/**
 * Build the de-dup id for a changeUserRole apiAction call.
 *
 * The callSignal in apiCall.js is `${successActionType}${id}`, so the loading
 * key in ChangeRoles.jsx must be `${CHANGE_USER_ROLE_SUCCESS}${changeUserRoleCallId(...)}`.
 * Exporting this function keeps the format in one place: if the id changes here,
 * the component automatically picks it up without silent drift.
 *
 * @param {string} userName
 * @param {string} action
 * @param {string} role
 * @returns {string}
 */
export const changeUserRoleCallId = (userName, action, role) =>
  `${userName}-${action}-${role}`

/**
 * Add or remove a single role from any user (developer-only tool).
 * On success the `watchChangeUserRole` saga dispatches `getUser()`.
 *
 * @param {string} userName
 * @param {'ADD'|'REMOVE'} action
 * @param {string} role
 */
export const changeUserRole = (userName, action, role) =>
  applicationActions.apiAction({
    asyncFn: users.changeUserRole,
    args: { userName, action, role },
    successActionType: CHANGE_USER_ROLE_SUCCESS,
    failureActionType: CHANGE_USER_ROLE_FAILURE,
    requestActionType: CHANGE_USER_ROLE_REQUEST,
    id: changeUserRoleCallId(userName, action, role),
    // changeUserRole uses GET because the endpoint cannot accept a request
    // body, but the operation IS a mutation (role change). The default handler
    // silently swallows GET failures; mutationErrorHandler overrides that so a
    // failure always shows the blocking error modal.
    errorHandlers: [mutationErrorHandler],
  })
